import React, { FC }                                                                                                             from 'react';
import * as Yup                                                                                                                  from "yup";
import { Cell, Grid }                                                                                                            from "styled-css-grid";
import { Checkbox, FieldWrapper, TextField }                                                                                     from "@src/views/components/forms";
import { BulkUpdateFormControlContainer }                                                                                        from "@src/views/pages/projects/activities/bulk/BulkUpdateFormControlContainer";
import { ProjectActivityBulkUpdateShell, ProjectActivityBulkUpdateShellFormContainerProps, ProjectActivityBulkUpdateShellProps } from "@src/views/pages/projects/activities/bulk/ProjectActivityBulkUpdateShell";
import { ProjectActivityBulkUpdateMultiDialogProps }                                                                             from "@src/views/pages/projects/operations/ProjectActivityBulkUpdateMultiDialog";
import { FieldLabel }                                                                                                            from "@src/views/components/forms/formik/FieldLabel";
import { ENTITY_ICONS }                                                                                                          from "@src/constants";
import _                                                                                                                         from "underscore";


type FormTypeAttributes = {
    operationalCost: number,
    operationalCostNote?: string,
    operationalCostReplacesProfessionalFees: boolean,
}

const formDefaultValues = {
    operationalCost:                         null,
    operationalCostNote:                     null,
    operationalCostReplacesProfessionalFees: null,
}

const validationSchema = (
    Yup.object().shape( {
        operationalCost: Yup.mixed()
                            .when( 'operationalCost', {
                                is:        value => !_.isEmpty( value ),
                                then:      Yup.number().min( 0, 'Must be zero or more.' ).required( 'Please enter a value' ),
                                otherwise: Yup.number().nullable()
                            } )
    }, [
        [ 'operationalCost', 'operationalCost' ]
    ] )
)

const mutationAttributeFormatter: ProjectActivityBulkUpdateShellProps<FormTypeAttributes>['prepareFormValuesForMutation'] = ( values, formik ) => {

    const {
              operationalCost,
              operationalCostNote,
              operationalCostReplacesProfessionalFees,
          } = values

    const changes: Record<any, any> = {};

    if( _.isNumber( operationalCost ) ){
        changes.operationalCost = operationalCost;
    }

    if( !_.isEmpty( operationalCostNote ) ){
        changes.operationalCostNote = operationalCostNote;
    }

    if( _.isBoolean( operationalCostReplacesProfessionalFees ) ){
        changes.operationalCostReplacesProfessionalFees = operationalCostReplacesProfessionalFees;
    }

    return changes;
}

interface StaticProjectActivityBulkUpdateOperationalCostProps extends Pick<ProjectActivityBulkUpdateShellProps<FormTypeAttributes>, 'supportModals' | 'supportToasts'>,
    Pick<ProjectActivityBulkUpdateMultiDialogProps, 'projectId' | 'projectVersionId' | 'projectActivityIds'> {
    formContainer: FC<ProjectActivityBulkUpdateShellFormContainerProps>
}

const StaticProjectActivityBulkUpdateOperationalCostDefaultProps = {
    supportModals: false,
    supportToasts: true,
} satisfies Partial<StaticProjectActivityBulkUpdateOperationalCostProps>

function StaticProjectActivityBulkUpdateOperationalCost( _props: StaticProjectActivityBulkUpdateOperationalCostProps ){

    const props = { ...StaticProjectActivityBulkUpdateOperationalCostDefaultProps, ..._props };

    return (

        <ProjectActivityBulkUpdateShell<FormTypeAttributes>
            projectId={ props.projectId }
            projectVersionId={ props.projectVersionId }
            ids={ props.projectActivityIds }
            supportToasts={ props.supportToasts }
            supportModals={ props.supportModals }
            defaultFormValues={ formDefaultValues }
            validationSchema={ validationSchema }
            formContainer={ props.formContainer }
            prepareFormValuesForMutation={ mutationAttributeFormatter }>

            {/* FORM CONTENT */ }
            { ( { formik, isProcessing, isLoading } ) => {

                return (

                    <BulkUpdateFormControlContainer
                        icon={ ENTITY_ICONS.BUDGET.OPERATIONAL_COSTS }
                        title={ 'Update Operational Cost' }
                        description={ `Migrate the operational cost for multiple activities to the one specified.` }>

                        <Grid columns={ `40% auto` } rows={ `auto auto` } columnGap={ "30px" } rowGap={'30px'}>
                            <Cell>

                                <TextField
                                    type="number"
                                    name={ "operationalCost" }
                                    label={ "Operational Cost" }
                                    descriptionText={ 'The operational cost assigned to each of the selected activities.' }
                                    placeholder="$0.00"
                                    min={ 0 }
                                    autoFocus={ true }
                                    disabled={ isLoading || isProcessing }/>

                            </Cell>
                            <Cell>

                                <TextField
                                    type="text"
                                    name={ "operationalCostNote" }
                                    label={ "Cost Note" }
                                    descriptionText={ `A single description for all selected activity's operational costs. This will replace any current notes.` }
                                    disabled={ isLoading || isProcessing }/>

                            </Cell>
                            <Cell width={ 2 }>

                                <FieldWrapper>

                                    <FieldLabel>Fee Behavior</FieldLabel>

                                    <Checkbox id={ "operationalCostReplacesProfessionalFees" }
                                              name={ "operationalCostReplacesProfessionalFees" }
                                              label="Operational Costs Replace Professional Fees"
                                              descriptionText={ "If specified, professional fees wont be included as part of each operational cost calculation." }
                                              disabled={ isLoading || isProcessing } />

                                </FieldWrapper>
                            </Cell>
                        </Grid>

                    </BulkUpdateFormControlContainer>

                )
            } }

        </ProjectActivityBulkUpdateShell>

    );
}

export {
    StaticProjectActivityBulkUpdateOperationalCost,
    StaticProjectActivityBulkUpdateOperationalCostDefaultProps,
    type StaticProjectActivityBulkUpdateOperationalCostProps,
};
